import React, { useCallback, useEffect, useState } from 'react';
import { digitalContractsService } from '../services/api';
import { fmtDate, fmtDateTime } from '../utils/formatters';
import EditorDeSignatarios from './EditorDeSignatarios';
import { SignerForm, errosDosSignatarios, payloadDeSigners, signersGravados } from '../utils/signatarios';
import { useConfirmarPerigo } from './shared/ConfirmDialog';
import { useAviso } from './shared/Aviso';
import { relatoDoLembrete, relatoDaFalhaDoLembrete } from '../utils/lembrete';
import {
  Aditivo,
  aditivoVigente,
  numeroDoAditivo,
  rotuloDoSubtipo,
  vigenciaEfetiva,
} from '../utils/aditivosDoContrato';

/**
 * TERMOS ADITIVOS DE UM CONTRATO.
 *
 * ⛔ POR QUE ESTA ABA EXISTE. O 2º Termo Aditivo do Sicoob Coopemata (contrato
 * 140) estava gravado em `digital_contracts` #54, pronto para assinar, e NÃO
 * APARECIA EM LUGAR NENHUM DA INTERFACE — alguém passou vinte minutos
 * procurando pelo navegador. A tabela nasceu do fluxo do CRM (lead → proposta →
 * venda nova) e tudo que a lê filtra por lead; aditivo de cliente de 2021 não
 * tem lead. O backend #236 abriu a rota por contrato, e esta é a porta dela.
 *
 * O que NÃO se faz aqui: gerar número (é do backend, com índice único por
 * contrato — sequencial gerado no cliente duplica quando duas abas criam
 * juntas) e consertar o PDF. A rota `:id/pdf` ainda renderiza aditivo com o
 * template de venda nova; é trabalho do backend, e a tela só chama a rota.
 */

const STATUS_META: Record<string, { cls: string; label: string }> = {
  'Rascunho': { cls: 'bg-slate-600/60 text-slate-300', label: 'Rascunho' },
  'Aguardando Assinatura': { cls: 'bg-amber-500/20 text-amber-400', label: 'Aguardando assinatura' },
  'Assinado': { cls: 'bg-green-500/20 text-green-400', label: 'Assinado' },
  'Cancelado': { cls: 'bg-red-500/20 text-red-400', label: 'Cancelado' },
};

/**
 * O ENVIO ESTEVE BLOQUEADO ATÉ 29/09/2026, e o registro fica porque a razão
 * volta a valer se alguém mexer no gerador.
 *
 * Enquanto o `send-for-signature` montava o documento com o modelo de venda
 * nova — "Proposta: N/A" e um segundo quadro de assinaturas com CNPJ de
 * exemplo —, clicar não produzia um rascunho feio: produzia o DOCUMENTO ERRADO
 * na mão de quatro signatários, e assinado ele vale.
 *
 * Liberado com o backend #237 (servidor em e2b1462). O que foi conferido no
 * controller compilado, e não só no aviso: `GET :id/pdf` e
 * `send-for-signature` chamam o MESMO `generateContractPdf(buildPdfData(...))`,
 * a capa sai por `capaDoDocumento` conforme o `documentKind`, e o arquivo que
 * sobe para a ClickSign é nomeado pelo rótulo do documento. Prévia e envio
 * saindo de renderizadores diferentes era o risco real — conferir a prévia não
 * diria nada sobre o que o cliente assina.
 */
const PDF_DE_ADITIVO_PRONTO = true;
const MOTIVO_DO_BLOQUEIO =
  'O gerador de PDF monta aditivo com o modelo de venda nova. Enviar colocaria o documento errado para assinatura.';

interface Props {
  contrato: any;
  canEdit?: boolean;
}

export default function AditivosDoContratoTab({ contrato, canEdit = true }: Props) {
  const confirmarPerigo = useConfirmarPerigo();
  const [aditivos, setAditivos] = useState<Aditivo[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const avisar = useAviso();
  const [baixando, setBaixando] = useState<number | null>(null);
  /** O aditivo aberto para conferir signatários e acompanhar a assinatura. */
  const [aberto, setAberto] = useState<Aditivo | null>(null);
  const [signers, setSigners] = useState<SignerForm[]>([]);
  const [salvando, setSalvando] = useState(false);
  const [statusPorSignatario, setStatusPorSignatario] = useState<any[] | null>(null);
  const [consultando, setConsultando] = useState(false);

  const contratoId = Number(contrato?.id) || 0;

  const carregar = useCallback(async () => {
    if (!contratoId) return;
    setCarregando(true); setErro(null);
    try {
      const r = await digitalContractsService.getByContract(contratoId);
      setAditivos(Array.isArray(r) ? r : (r?.data || []));
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Não foi possível carregar os termos aditivos');
    } finally { setCarregando(false); }
  }, [contratoId]);

  useEffect(() => { carregar(); }, [carregar]);

  const vigencia = vigenciaEfetiva(contrato, aditivos);
  const vigente = aditivoVigente(aditivos);

  const verPdf = async (a: Aditivo) => {
    setBaixando(a.id);
    try {
      // Assinado: a URL da ClickSign vem por JSON e abre direto (o S3 deles não
      // manda CORS, então não dá para seguir o 302 por XHR).
      if (a.status === 'Assinado') {
        const { url } = await digitalContractsService.getSignedUrl(a.id);
        if (url) { window.open(url, '_blank', 'noopener'); return; }
      }
      const blob = await digitalContractsService.getPdf(a.id);
      const url = URL.createObjectURL(blob as any);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Não foi possível abrir o PDF');
    } finally { setBaixando(null); }
  };

  /**
   * Abre o painel JÁ COM os signatários gravados. O #54 tem três, com CPF,
   * conferidos por gente — nascer vazio faria alguém redigitar, e redigitar CPF
   * de terceiro é onde o erro entra.
   */
  const abrir = (a: Aditivo) => {
    setAberto(a);
    setSigners(signersGravados(a.signers));
    setStatusPorSignatario(null);
    setErro(null);
  };

  const salvarSignatarios = async () => {
    if (!aberto) return;
    const erros = errosDosSignatarios(signers);
    if (erros.length) { setErro(erros.join(' · ')); return; }
    setSalvando(true); setErro(null);
    try {
      await digitalContractsService.update(aberto.id, { signers: payloadDeSigners(signers) });
      await carregar();
      setAberto((prev) => (prev ? { ...prev, signers: payloadDeSigners(signers) } : prev));
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Não foi possível salvar os signatários');
    } finally { setSalvando(false); }
  };

  /**
   * O "histórico" que existe para documento de assinatura: o estado de cada
   * signatário na ClickSign mais as datas do próprio registro. Não há tabela de
   * eventos por documento — e inventar uma linha do tempo a partir de três
   * carimbos seria contar uma história que ninguém registrou.
   */
  const conferirStatus = async () => {
    if (!aberto) return;
    setConsultando(true); setErro(null);
    try {
      const r = await digitalContractsService.getSignersStatus(aberto.id);
      setStatusPorSignatario(Array.isArray(r) ? r : (r?.signers || []));
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Não foi possível consultar o status');
    } finally { setConsultando(false); }
  };

  /**
   * Gera o PDF e manda para a ClickSign. Salva os signatários ANTES: o corpo do
   * envio leva a lista da tela, e deixar o banco para trás faria a próxima
   * abertura mostrar gente diferente de quem recebeu o convite.
   */
  const enviarParaAssinatura = async () => {
    if (!aberto) return;
    const erros = errosDosSignatarios(signers);
    if (erros.length) { setErro(erros.join(' · ')); return; }
    const quem = signers.map((s) => `• ${s.name} (${s.email})`).join('\n');
    if (!window.confirm(`Enviar o ${numeroDoAditivo(aberto)} Termo Aditivo para assinatura?\n\n${quem}\n\nCada um recebe um link individual da ClickSign.`)) return;
    setSalvando(true); setErro(null);
    try {
      const payload = payloadDeSigners(signers);
      await digitalContractsService.update(aberto.id, { signers: payload });
      const r = await digitalContractsService.sendForSignature(aberto.id, { signers: payload });
      if (r?.errors?.length) {
        setErro(`Enviado com falhas: ${r.errors.map((e: any) => `${e.name || e.email}: ${e.error}`).join(' · ')}`);
      }
      await carregar();
      setAberto(null);
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Não foi possível enviar para assinatura');
    } finally { setSalvando(false); }
  };

  const reenviarLembrete = async () => {
    if (!aberto) return;
    setConsultando(true); setErro(null);
    try {
      // O relato lê a resposta: antes o success:false passava por "reenviado" num window.alert (fase 0, 23/10/2026).
      const { texto, tom } = relatoDoLembrete(await digitalContractsService.resendSignatureNotification(aberto.id));
      avisar(texto, tom);
    } catch (e: any) {
      const { texto, tom } = relatoDaFalhaDoLembrete(e);
      avisar(texto, tom);
    } finally { setConsultando(false); }
  };

  const cancelarAssinatura = async () => {
    if (!aberto) return;
    if (!await confirmarPerigo('Cancelar a assinatura deste termo aditivo?\n\nOs links já enviados param de valer e o documento volta para Rascunho.')) return;
    setConsultando(true); setErro(null);
    try {
      await digitalContractsService.cancelSignature(aberto.id);
      await carregar();
      setAberto(null);
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Não foi possível cancelar a assinatura');
    } finally { setConsultando(false); }
  };

  return (
    <div className='space-y-4'>
      <div className='flex items-start justify-between gap-4 flex-wrap'>
        <div>
          <h3 className='text-base font-semibold text-white'>Termos aditivos</h3>
          <p className='text-xs text-slate-500 mt-0.5'>
            Renovação, reajuste e alteração deste contrato. O número que aparece é o do
            documento, não o do sistema.
          </p>
        </div>
        {/* A vigência que VALE, com a origem ao lado. As fontes são heterogêneas
            — contrato, proposta aprovada, e-mail, ou derivação sem cláusula —, e
            cabeçalho que não diz a origem faz a data parecer sempre documental. */}
        <div className='text-right'>
          <p className='text-[11px] font-mono uppercase tracking-wide text-slate-500'>Vigência efetiva</p>
          <p className='text-sm text-white'>{vigencia.fim ? fmtDate(vigencia.fim) : '—'}</p>
          <p className='text-xs text-slate-500'>segundo o {vigencia.fonte}</p>
        </div>
      </div>

      {erro && (
        <div className='text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2'>{erro}</div>
      )}

      {carregando ? (
        <p className='text-sm text-slate-500'>Carregando…</p>
      ) : !aditivos.length ? (
        <div className='rounded-lg border border-dashed border-slate-700 p-5 text-center'>
          <p className='text-sm text-slate-400'>Nenhum termo aditivo neste contrato.</p>
          <p className='text-xs text-slate-500 mt-1'>
            Aditivo assinado fora do HUB continua valendo — mas só aparece aqui depois de cadastrado.
          </p>
        </div>
      ) : (
        <div className='rounded-lg border border-slate-700 overflow-hidden'>
          <div className='flex items-center gap-3 px-3 py-2 bg-slate-900/70 text-[11px] uppercase text-slate-500 font-semibold'>
            <span className='w-16'>Número</span>
            <span className='flex-1'>O que faz</span>
            <span className='w-44'>Vigência que estabelece</span>
            <span className='w-40'>Status</span>
            <span className='w-24 text-right'>Documento</span>
          </div>
          {aditivos.map((a) => {
            const meta = STATUS_META[String(a.status || '')] || { cls: 'bg-slate-700 text-slate-300', label: String(a.status || '—') };
            const ehVigente = vigente?.id === a.id;
            const subtipos = Array.isArray(a.aditivoSubtipos) ? a.aditivoSubtipos : [];
            return (
              <div key={a.id} className='flex items-center gap-3 px-3 py-2.5 border-t border-slate-700/60 hover:bg-slate-800/40'>
                <span className='w-16 text-sm text-white font-medium'>{numeroDoAditivo(a)}</span>
                <span className='flex-1 min-w-0 flex flex-wrap gap-1'>
                  {subtipos.length ? subtipos.map((s) => (
                    <span key={s} className='text-[11px] px-1.5 py-0.5 rounded-full bg-slate-700 text-slate-300'>
                      {rotuloDoSubtipo(s)}
                    </span>
                  )) : <span className='text-xs text-slate-600'>não declarado</span>}
                </span>
                <span className='w-44 text-xs text-slate-300'>
                  {a.vigenciaInicio || a.vigenciaFim ? (
                    <>
                      {a.vigenciaInicio ? fmtDate(a.vigenciaInicio) : '—'} a {a.vigenciaFim ? fmtDate(a.vigenciaFim) : '—'}
                      {ehVigente && <span className='ml-1 text-[10px] text-green-400'>· vigente</span>}
                    </>
                  ) : <span className='text-slate-600'>não altera prazo</span>}
                </span>
                <span className='w-40'>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full ${meta.cls}`}>{meta.label}</span>
                  {a.signedAt && <span className='block text-[10px] text-slate-500 mt-0.5'>em {fmtDate(a.signedAt)}</span>}
                </span>
                <span className='w-24 flex items-center justify-end gap-1'>
                  <button type='button' onClick={() => verPdf(a)} disabled={baixando === a.id}
                    className='px-2 py-1 text-xs rounded bg-slate-700 hover:bg-slate-600 text-slate-200 disabled:opacity-50'
                    title={a.status === 'Assinado' ? 'Abrir o PDF assinado' : 'Abrir a minuta em PDF'}>
                    {baixando === a.id ? '…' : 'PDF'}
                  </button>
                  <button type='button' onClick={() => abrir(a)}
                    className='px-2 py-1 text-xs rounded bg-purple-600/80 hover:bg-purple-600 text-white'
                    title='Signatários e acompanhamento da assinatura'>
                    Assinatura
                  </button>
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* ── SIGNATÁRIOS E ACOMPANHAMENTO ── */}
      {aberto && (
        <div className='rounded-xl border border-purple-500/30 bg-slate-900/60 p-4 space-y-4'>
          <div className='flex items-start justify-between gap-3 flex-wrap'>
            <div>
              <p className='text-sm font-semibold text-white'>
                {numeroDoAditivo(aberto)} Termo Aditivo · assinatura
              </p>
              <p className='text-xs text-slate-500'>
                Documento #{aberto.id} · criado {fmtDateTime(aberto.createdAt)}
                {aberto.signedAt && <> · assinado {fmtDateTime(aberto.signedAt)}</>}
              </p>
            </div>
            <button type='button' onClick={() => setAberto(null)} className='text-slate-400 hover:text-white text-sm px-2'>fechar</button>
          </div>

          <EditorDeSignatarios
            signers={signers}
            onChange={setSigners}
            disabled={!canEdit || aberto.status === 'Assinado'}
          />

          {statusPorSignatario && (
            <div className='rounded-lg border border-slate-700 overflow-hidden'>
              <p className='px-3 py-2 bg-slate-800/60 text-[11px] font-mono uppercase tracking-wide text-slate-500'>
                Na ClickSign, agora
              </p>
              {statusPorSignatario.length === 0 && (
                <p className='px-3 py-2 text-xs text-slate-500'>Nada enviado ainda.</p>
              )}
              {statusPorSignatario.map((s: any, i: number) => (
                <div key={i} className='flex items-center justify-between gap-3 px-3 py-2 border-t border-slate-700/60'>
                  <span className='text-sm text-slate-200 truncate'>{s.name || s.email}</span>
                  <span className='text-xs whitespace-nowrap'>
                    {s.signed || s.status === 'signed'
                      ? <span className='text-green-400'>assinou{s.signedAt ? ` em ${fmtDateTime(s.signedAt)}` : ''}</span>
                      : <span className='text-amber-400'>pendente</span>}
                  </span>
                </div>
              ))}
            </div>
          )}

          <div className='flex flex-wrap items-center gap-2'>
            {canEdit && aberto.status !== 'Assinado' && (
              <button type='button' onClick={salvarSignatarios} disabled={salvando}
                className='px-3 py-1.5 text-xs rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200 disabled:opacity-50'>
                {salvando ? 'Salvando…' : 'Salvar signatários'}
              </button>
            )}
            {canEdit && aberto.status !== 'Assinado' && (
              // Existe e explica por que não clica: ver PDF_DE_ADITIVO_PRONTO.
              <button type='button' onClick={enviarParaAssinatura} disabled={!PDF_DE_ADITIVO_PRONTO || salvando}
                title={PDF_DE_ADITIVO_PRONTO ? 'Gera o PDF e envia aos signatários pela ClickSign' : MOTIVO_DO_BLOQUEIO}
                className='px-3 py-1.5 text-xs rounded-lg bg-primary hover:bg-primary-strong text-white disabled:bg-slate-800 disabled:text-slate-500 disabled:cursor-not-allowed'>
                Enviar para assinatura
              </button>
            )}
            <button type='button' onClick={conferirStatus} disabled={consultando}
              className='px-3 py-1.5 text-xs rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200 disabled:opacity-50'>
              {consultando ? 'Consultando…' : 'Verificar status'}
            </button>
            {canEdit && aberto.status === 'Aguardando Assinatura' && (
              <>
                <button type='button' onClick={reenviarLembrete} disabled={consultando}
                  className='px-3 py-1.5 text-xs rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200 disabled:opacity-50'>
                  Reenviar lembrete
                </button>
                <button type='button' onClick={cancelarAssinatura} disabled={consultando}
                  className='px-3 py-1.5 text-xs rounded-lg bg-red-500/15 hover:bg-red-500/25 text-red-300'>
                  Cancelar assinatura
                </button>
              </>
            )}
          </div>

          {!PDF_DE_ADITIVO_PRONTO && aberto.status !== 'Assinado' && (
            <p className='text-[11px] text-amber-300/90 bg-amber-500/10 border border-amber-500/25 rounded-lg px-3 py-2'>
              <b>Envio ainda bloqueado.</b> {MOTIVO_DO_BLOQUEIO} Os signatários já podem ser conferidos e
              salvos agora — é o que o envio vai usar.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
